import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Plus, Pencil, Trash2, Clock, Zap, Globe, X } from "lucide-react";

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", UAH: "₴" };

const emptyTariff = {
  name: "",
  price: 0,
  currency: "EUR",
  is_active: true,
  billing_type: "monthly",
  included_hours: "",
  response_time_label: "",
  sites_included_label: "",
  additional_site_price: "",
  additional_site_currency: "EUR",
  working_hours_label: "",
  description: "",
  features: [],
};

const fmtPrice = (t) => {
  const sym = CURRENCY_SYMBOLS[t.currency || "EUR"] || (t.currency || "EUR") + " ";
  const num = Number(t.price || 0).toLocaleString();
  if (t.billing_type === "hourly") return `${sym}${num} / hr`;
  return `${sym}${num} / mo`;
};

const SupportTariffsPage = () => {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTariff);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [featureDraft, setFeatureDraft] = useState("");

  const fetchTariffs = async () => {
    try {
      const res = await api.get("/support-tariffs");
      setTariffs(res.data || []);
    } catch (e) {
      toast.error("Failed to load tariffs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTariffs();
  }, []);

  const open = (tariff = null) => {
    if (tariff) {
      setEditing(tariff);
      setForm({
        name: tariff.name || "",
        price: tariff.price || 0,
        currency: tariff.currency || "EUR",
        is_active: tariff.is_active !== false,
        billing_type: tariff.billing_type || "monthly",
        included_hours: tariff.included_hours ?? "",
        response_time_label: tariff.response_time_label || "",
        sites_included_label: tariff.sites_included_label || "",
        additional_site_price: tariff.additional_site_price ?? "",
        additional_site_currency: tariff.additional_site_currency || tariff.currency || "EUR",
        working_hours_label: tariff.working_hours_label || "",
        description: tariff.description || "",
        features: Array.isArray(tariff.features) ? [...tariff.features] : [],
      });
    } else {
      setEditing(null);
      setForm(emptyTariff);
    }
    setFeatureDraft("");
    setDialogOpen(true);
  };

  const addFeature = () => {
    const v = featureDraft.trim();
    if (!v) return;
    setForm((p) => ({ ...p, features: [...p.features, v] }));
    setFeatureDraft("");
  };

  const removeFeature = (idx) => {
    setForm((p) => ({ ...p, features: p.features.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: form.name.trim(),
        price: parseFloat(form.price) || 0,
        currency: form.currency || "EUR",
        is_active: !!form.is_active,
        billing_type: form.billing_type || "monthly",
        included_hours:
          form.included_hours === "" || form.included_hours === null
            ? null
            : parseFloat(form.included_hours),
        response_time_label: form.response_time_label?.trim() || null,
        sites_included_label: form.sites_included_label?.trim() || null,
        additional_site_price:
          form.additional_site_price === "" || form.additional_site_price === null
            ? null
            : parseFloat(form.additional_site_price),
        additional_site_currency: form.additional_site_currency || null,
        working_hours_label: form.working_hours_label?.trim() || null,
        description: form.description?.trim() || null,
        features: (form.features || []).map((f) => f.trim()).filter(Boolean),
      };
      if (editing) {
        await api.put(`/support-tariffs/${editing.id}`, data);
        toast.success("Tariff updated");
      } else {
        await api.post("/support-tariffs", data);
        toast.success("Tariff created");
      }
      setDialogOpen(false);
      fetchTariffs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tariff) => {
    if (!window.confirm(`Deactivate "${tariff.name}"? Existing clients keep their link, future invoices won't pick it.`)) return;
    try {
      await api.delete(`/support-tariffs/${tariff.id}`);
      toast.success("Tariff deactivated");
      fetchTariffs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deactivate");
    }
  };

  const visible = showInactive ? tariffs : tariffs.filter((t) => t.is_active !== false);

  return (
    <div className="space-y-6" data-testid="support-tariffs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-3">
            <LifeBuoy className="h-7 w-7 text-emerald-400" />
            Support Tariffs
          </h1>
          <p className="text-zinc-400 mt-1">
            Catalog of support tiers. Aligned with the Smartorange technical-support
            commercial proposal (Standard €150 → Urgent €900 + Pay-as-you-go €30/hr).
          </p>
        </div>
        <Button onClick={() => open()} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Tariff
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-emerald-500"
          />
          Show inactive
        </label>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Name</TableHead>
                <TableHead className="text-zinc-400 text-right">Price</TableHead>
                <TableHead className="text-zinc-400">Hours</TableHead>
                <TableHead className="text-zinc-400">Response</TableHead>
                <TableHead className="text-zinc-400">Sites</TableHead>
                <TableHead className="text-zinc-400">Schedule</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-zinc-500">Loading…</TableCell></TableRow>
              ) : visible.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-zinc-500">No tariffs yet</TableCell></TableRow>
              ) : (
                visible.map((t) => (
                  <TableRow key={t.id} className="border-zinc-800 hover:bg-zinc-800/40 align-top">
                    <TableCell className="text-zinc-50 font-medium">
                      <div>{t.name}</div>
                      {t.description && (
                        <div className="text-xs text-zinc-500 mt-0.5 max-w-[280px]">{t.description}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-emerald-300 font-mono whitespace-nowrap">
                      {fmtPrice(t)}
                      {t.additional_site_price ? (
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          +{CURRENCY_SYMBOLS[t.additional_site_currency || t.currency || "EUR"] || ""}
                          {Number(t.additional_site_price).toLocaleString()} / site
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm whitespace-nowrap">
                      {t.included_hours != null ? `${t.included_hours} h` : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm">
                      {t.response_time_label ? (
                        <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-amber-400" />{t.response_time_label}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm">
                      {t.sites_included_label ? (
                        <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3 text-sky-400" />{t.sites_included_label}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm">
                      {t.working_hours_label ? (
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-emerald-400" />{t.working_hours_label}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell>
                      {t.is_active === false ? (
                        <Badge className="bg-zinc-700 text-zinc-300">Inactive</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => open(t)} className="text-zinc-400 hover:text-zinc-50">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {t.is_active !== false && (
                          <Button variant="ghost" size="icon" onClick={() => remove(t)} className="text-rose-400 hover:text-rose-300">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              {editing ? "Edit Tariff" : "Add Tariff"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Standard / Optimal / …"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Billing Type</Label>
                <Select value={form.billing_type} onValueChange={(v) => setForm({ ...form, billing_type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="monthly">Monthly subscription</SelectItem>
                    <SelectItem value="hourly">Per-hour (pay as you go)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">{form.billing_type === "hourly" ? "Hourly Rate *" : "Monthly Price *"}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UAH">UAH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Included Hours / mo</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  disabled={form.billing_type === "hourly"}
                  value={form.included_hours}
                  onChange={(e) => setForm({ ...form, included_hours: e.target.value })}
                  placeholder={form.billing_type === "hourly" ? "n/a" : "7.5"}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 disabled:opacity-40"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Response Time Label</Label>
                <Input
                  value={form.response_time_label}
                  onChange={(e) => setForm({ ...form, response_time_label: e.target.value })}
                  placeholder="15 minutes / 2 hours / 24 hours"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Sites Included</Label>
                <Input
                  value={form.sites_included_label}
                  onChange={(e) => setForm({ ...form, sites_included_label: e.target.value })}
                  placeholder="1 / 1+ / 2+"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-zinc-400 text-xs">Additional Site Price (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.additional_site_price}
                  onChange={(e) => setForm({ ...form, additional_site_price: e.target.value })}
                  placeholder="60"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Add. Site Currency</Label>
                <Select
                  value={form.additional_site_currency || "EUR"}
                  onValueChange={(v) => setForm({ ...form, additional_site_currency: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UAH">UAH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Working Hours Label</Label>
              <Input
                value={form.working_hours_label}
                onChange={(e) => setForm({ ...form, working_hours_label: e.target.value })}
                placeholder="08:00–16:00 Mon–Fri"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Short Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="One-line marketing description shown in the catalog & client picker"
                rows={2}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Features (each = one bullet in offers)</Label>
              <div className="flex gap-2">
                <Input
                  value={featureDraft}
                  onChange={(e) => setFeatureDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addFeature();
                    }
                  }}
                  placeholder="Type a feature and press Enter"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
                <Button type="button" onClick={addFeature} variant="outline" className="border-zinc-700">
                  Add
                </Button>
              </div>
              {form.features.length > 0 && (
                <div className="mt-2 space-y-1">
                  {form.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 group bg-zinc-800/50 rounded px-2 py-1 text-sm text-zinc-200">
                      <span className="text-emerald-400 mt-0.5">•</span>
                      <span className="flex-1">{f}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(i)}
                        className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-400 transition"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {editing && (
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="accent-emerald-500"
                />
                Active (visible in client picker)
              </label>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTariffsPage;
